import React from 'react';
import { Link } from 'react-router-dom';
import { Upload, Bot, Send, ShieldCheck, Clock, Globe, ArrowRight, CheckCircle2, Star } from 'lucide-react';
import BrandLogo from '../components/brand/BrandLogo';

const features = [
  { icon: Upload, title: 'Upload Once', text: 'Bulk upload 500+ catalog images. AI overlays codes & smart-tags everything.' },
  { icon: Bot, title: 'AI Sales Agent', text: 'Claude-powered AI replies to every WhatsApp inquiry instantly, 24×7.' },
  { icon: Send, title: 'Catalog Broadcast', text: 'Push catalogs to unlimited WhatsApp groups with anti-spam rate limiting.' },
  { icon: ShieldCheck, title: 'Live Stock Engine', text: '20-min reservations, atomic deduction — never oversell again.' },
  { icon: Clock, title: 'Payment + Shipping', text: 'Screenshot verification, courier tracking, auto-notify buyers.' },
  { icon: Globe, title: 'Multilingual', text: 'Hindi, Gujarati, Tamil, English — meet buyers in their language.' },
];

const steps = [
  { n: '01', title: 'Upload Catalog', text: 'Drag-drop 500 images. AI generates KUR-1001, SAR-2001 codes & overlays them.' },
  { n: '02', title: 'AI Processes', text: 'Smart tagging, deduplication, similarity search, stock setup — all automated.' },
  { n: '03', title: 'Automate Sales', text: 'Broadcast to groups. AI handles every inquiry. You confirm payments & ship.' },
];

export default function Landing() {
  return (
    <div className="min-h-screen mesh-bg">
      {/* Nav */}
      <header className="sticky top-0 z-30 backdrop-blur bg-white/70 border-b border-fab-primary-lighter">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/home" className="flex items-center" data-testid="logo-link">
            <BrandLogo height={32} />
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-[13px] text-fab-text-secondary font-medium">
            <a href="#features" className="hover:text-fab-primary">Features</a>
            <a href="#how" className="hover:text-fab-primary">How It Works</a>
            <Link to="/pricing" className="hover:text-fab-primary">Pricing</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-login" className="text-[13px] font-semibold text-fab-text px-3 py-2">Login</Link>
            <Link to="/signup" data-testid="nav-signup" className="btn-primary text-[13px]">Start Free Trial</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-fab-primary-lighter text-fab-primary text-[11px] font-semibold uppercase tracking-wider mb-6">
            <span className="w-1.5 h-1.5 bg-fab-wa rounded-full live-dot" /> India's Trusted Wholesale Fabric Hub
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-[58px] font-extrabold text-fab-text leading-[1.05] tracking-tight">
            Upload catalogs once. <span className="gradient-text">Broadcast everywhere.</span>
          </h1>
          <p className="mt-6 text-[16px] text-fab-text-secondary leading-relaxed max-w-xl">
            Automate textile WhatsApp sales, stock, and orders with AI. Built exclusively for Indian textile wholesalers — never leave WhatsApp.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/signup" data-testid="hero-cta" className="btn-primary text-[14px] flex items-center gap-2">Start Free Trial <ArrowRight className="w-4 h-4" /></Link>
            <a href="#features" className="px-5 py-2.5 rounded-lg border border-fab-primary-lighter bg-white text-fab-text text-[14px] font-semibold hover:bg-fab-elevated">Watch Demo</a>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-[12px] text-fab-text-secondary">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-fab-wa" /> 15 Days Free Trial</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-fab-wa" /> No Contracts</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-fab-wa" /> Dedicated Onboarding</span>
          </div>
        </div>
        <div className="relative">
          <div className="absolute -inset-6 bg-gradient-to-br from-fab-primary/20 to-fab-wa/20 blur-3xl rounded-full" />
          <div className="relative fab-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[12px] font-semibold text-fab-text-muted uppercase tracking-wider">Today's snapshot</div>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-fab-wa-light text-fab-wa-dark font-semibold">LIVE</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-fab-elevated p-4">
                <div className="text-[11px] text-fab-text-muted">Orders</div>
                <div className="font-display text-2xl font-bold text-fab-text">1,278</div>
                <div className="text-[11px] text-fab-success font-semibold">↗ 25.4%</div>
              </div>
              <div className="rounded-xl bg-fab-elevated p-4">
                <div className="text-[11px] text-fab-text-muted">Revenue</div>
                <div className="font-display text-2xl font-bold text-fab-text">₹24.75L</div>
                <div className="text-[11px] text-fab-success font-semibold">↗ 32.7%</div>
              </div>
              <div className="rounded-xl bg-fab-elevated p-4 col-span-2 flex items-center gap-3">
                <Bot className="w-8 h-8 text-fab-primary" />
                <div className="flex-1">
                  <div className="text-[12px] font-semibold text-fab-text">AI handled 8,450 messages</div>
                  <div className="text-[11px] text-fab-text-muted">28 hot leads detected this week</div>
                </div>
                <span className="w-2 h-2 rounded-full bg-fab-wa live-dot" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-[12px] text-fab-primary font-bold uppercase tracking-widest">Everything you need</div>
          <h2 className="font-display text-3xl lg:text-4xl font-extrabold text-fab-text mt-2">Six things FABTEXS AI does — so you don't have to</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <div key={i} className="fab-card p-6 hover:shadow-md transition-shadow">
              <div className="w-11 h-11 rounded-xl bg-fab-primary-lighter flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-fab-primary" />
              </div>
              <h3 className="font-display text-[16px] font-bold text-fab-text mb-1.5">{f.title}</h3>
              <p className="text-[13px] text-fab-text-secondary leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-[12px] text-fab-primary font-bold uppercase tracking-widest">How it works</div>
          <h2 className="font-display text-3xl lg:text-4xl font-extrabold text-fab-text mt-2">Live in 10 minutes</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {steps.map((s, i) => (
            <div key={i} className="fab-card p-7 relative">
              <div className="font-mono text-[44px] font-bold text-fab-primary-lighter leading-none">{s.n}</div>
              <h3 className="font-display text-[18px] font-bold text-fab-text mt-3">{s.title}</h3>
              <p className="text-[13px] text-fab-text-secondary mt-2 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonial */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="fab-card p-10 text-center bg-gradient-to-br from-fab-primary-lighter to-white">
          <div className="flex justify-center mb-4">{[...Array(5)].map((_,i)=> <Star key={i} className="w-5 h-5 fill-fab-warning text-fab-warning" />)}</div>
          <p className="font-display text-[20px] leading-relaxed text-fab-text">"From 14 hours/day on WhatsApp to 2 hours of approvals. Our revenue went up 38% in 2 months."</p>
          <div className="mt-4 text-[13px] text-fab-text-secondary">Mahesh Patel · MK Textiles · Surat</div>
        </div>
      </section>

      <footer className="border-t border-fab-primary-lighter mt-10 py-10 bg-white/60">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-[12px] text-fab-text-muted">
          <div>© 2026 FABTEXS AI. Built in India for Indian textile wholesalers.</div>
          <div className="flex gap-5">
            <Link to="/pricing">Pricing</Link>
            <a href="#features">Features</a>
            <Link to="/login">Login</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
